import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Phone, ChevronDown, Menu, X } from 'lucide-react';
import { companyInfo, navLinks } from '../data/siteData';
import Logo from './Logo';
import { luxuryEase } from '../lib/motion';

export default function Header({ onNavigate, visible = true }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (section) => {
    setMobileOpen(false);
    setDropdownOpen(false);
    onNavigate(section);
  };

  const solid = scrolled || mobileOpen;

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={visible ? { y: 0, opacity: 1 } : { y: -24, opacity: 0 }}
      transition={{ duration: 0.9, ease: luxuryEase }}
      className={`fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)] transition-all duration-500 ${
        visible ? '' : 'pointer-events-none'
      } ${
        solid
          ? 'bg-nosta-paper/92 backdrop-blur-md border-b border-nosta-line'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px] md:h-[84px]">
          <button onClick={() => go('home')} className="flex-shrink-0" aria-label={companyInfo.name}>
            <Logo inverted={!solid} className="h-11 sm:h-14 lg:h-16 w-auto" />
          </button>

          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.section}
                onClick={() => go(link.section)}
                className={`relative text-[13px] tracking-wideish uppercase font-medium transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-nosta-gold after:transition-all hover:after:w-full ${
                  solid ? 'text-nosta-ink/80 hover:text-nosta-ink' : 'text-white/85 hover:text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="relative hidden lg:block">
              <button
                onClick={() => setDropdownOpen((v) => !v)}
                className={`flex items-center gap-2 px-5 py-2.5 text-[13px] tracking-wide font-medium transition-all duration-500 ${
                  solid
                    ? 'bg-nosta-ink text-white hover:bg-nosta-espresso'
                    : 'bg-white/10 text-white border border-white/30 hover:bg-white hover:text-nosta-ink backdrop-blur-sm'
                }`}
              >
                <Phone size={15} />
                <span>Bize Ulaşın</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.35, ease: luxuryEase }}
                    className="absolute right-0 mt-3 w-80 bg-nosta-paper text-nosta-ink shadow-lift border border-nosta-line p-2 z-50"
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    {companyInfo.team.map((member, i) => (
                      <a
                        key={member.name}
                        href={`tel:${member.phone.replace(/\s/g, '')}`}
                        className={`block p-4 hover:bg-white transition-colors ${
                          i < companyInfo.team.length - 1 ? 'border-b border-nosta-line' : ''
                        }`}
                      >
                        <div className="eyebrow mb-1">{member.role}</div>
                        <div className="font-display text-xl font-semibold">{member.name}</div>
                        <div className="text-sm text-nosta-mute mt-0.5">{member.title}</div>
                        <div className="text-sm text-nosta-gold mt-2">{member.phone}</div>
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              className={`lg:hidden p-2 ${solid ? 'text-nosta-ink' : 'text-white'}`}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menü"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: luxuryEase }}
            className="lg:hidden overflow-hidden bg-nosta-paper border-t border-nosta-line"
          >
            <div className="px-6 py-6 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.section}
                  onClick={() => go(link.section)}
                  className="block w-full text-left py-3 font-display text-2xl text-nosta-ink"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 border-t border-nosta-line mt-4 space-y-3">
                {companyInfo.team.map((member) => (
                  <a
                    key={member.name}
                    href={`tel:${member.phone.replace(/\s/g, '')}`}
                    className="block py-2"
                  >
                    <div className="eyebrow">{member.role}</div>
                    <div className="font-medium">{member.name}</div>
                    <div className="text-sm text-nosta-gold">{member.phone}</div>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
